import clsx from "clsx";
import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02FreeIcons, CircleFreeIcons } from "@hugeicons/core-free-icons";

import { ErrorMessage } from "@/components/Input.tsx";
import { i18n } from "@/localization";
import type { TranslationKey } from "@/localization/resources/keys.ts";
import { PASSWORD_RULES, containsEmail } from "@/utils/passwordRules.ts";

interface PasswordStrengthIndicatorProps {
	password: string;
	email: string;
}

interface StrengthTier {
	labelKey: TranslationKey;
	barClass: string;
	textClass: string;
}

const VERY_WEAK: StrengthTier = {
	labelKey: "step3.passwordStrength.veryWeak",
	barClass: "bg-red-500",
	textClass: "text-red-500"
};
const WEAK: StrengthTier = {
	labelKey: "step3.passwordStrength.weak",
	barClass: "bg-red-500",
	textClass: "text-red-500"
};
const GOOD: StrengthTier = {
	labelKey: "step3.passwordStrength.good",
	barClass: "bg-yellow-500",
	textClass: "text-yellow-500"
};
const STRONG: StrengthTier = {
	labelKey: "step3.passwordStrength.strong",
	barClass: "bg-green-500",
	textClass: "text-green-600"
};

// Ordered from weakest to strongest; a tier fills as many bar segments as its position + 1,
// so the bar always has one segment per tier and only fills completely on the top tier.
const TIERS: StrengthTier[] = [VERY_WEAK, WEAK, GOOD, STRONG];

// Maps the number of met rules (index) to a tier; must have PASSWORD_RULES.length + 1 entries.
// Index 0 (no rule met) has no tier, so nothing is labelled and every segment stays neutral.
const TIER_BY_MET_COUNT: (StrengthTier | null)[] = [null, VERY_WEAK, VERY_WEAK, WEAK, GOOD, STRONG];

export function PasswordStrengthIndicator({ password, email }: PasswordStrengthIndicatorProps) {
	const met = PASSWORD_RULES.map((rule) => rule.test(password));
	const metCount = met.filter(Boolean).length;
	const emailViolation = containsEmail(password, email);

	// A password containing the e-mail address is never rated "strong", even if every rule is met.
	const rawTier = TIER_BY_MET_COUNT[metCount];
	const tier = emailViolation && rawTier === STRONG ? GOOD : rawTier;
	const filledSegments = tier ? TIERS.indexOf(tier) + 1 : 0;

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-center gap-3">
				<div className="flex flex-1 gap-1.5">
					{TIERS.map((_, i) => (
						<div
							key={i}
							className={clsx(
								"h-1.5 flex-1 rounded-full transition-colors duration-150",
								i < filledSegments && tier ? tier.barClass : "bg-brand-dark/10"
							)}
						/>
					))}
				</div>
				{tier && (
					<span className={clsx("text-xs font-medium whitespace-nowrap", tier.textClass)}>
						{i18n(tier.labelKey)}
					</span>
				)}
			</div>

			<ul className="flex flex-col gap-1.5">
				{PASSWORD_RULES.map((rule, i) => (
					<li
						key={rule.key}
						className={clsx(
							"flex items-center gap-2 text-xs transition-colors duration-150",
							met[i] ? "text-green-600" : "text-brand-dark/70"
						)}
					>
						<HugeiconsIcon icon={met[i] ? CheckmarkCircle02FreeIcons : CircleFreeIcons} size={16} />
						<span>{i18n(rule.labelKey)}</span>
					</li>
				))}
			</ul>

			{emailViolation && <ErrorMessage>{i18n("validation.passwordContainsEmail")}</ErrorMessage>}
		</div>
	);
}
